import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import '../../css/Pages.css';

const ScoringConfiguration = () => {
    const toast = useRef(null);
    const today = useMemo(() => new Date().toISOString().split('T')[0], []);

    const [isSavingConfig, setIsSavingConfig] = useState(false);
    const [configVersions, setConfigVersions] = useState([]);

    const [formMode, setFormMode] = useState('create');
    const [activeConfigId, setActiveConfigId] = useState(null);

    const isViewMode = formMode === 'view';
    const isEditMode = formMode === 'edit';

    const [newConfigMeta, setNewConfigMeta] = useState({
        VersionName: '',
        ApplyFromDate: null,
        ApplyToDate: null
    });

    const [scoringConfig, setScoringConfig] = useState({
        HoursPerPoint: 600,
        PrimaryAuthorDivisor: 3.0,
        MultiOrgPenalty: 0.5,
        Article_Q1: 3.0, Article_Q2: 2.75, Article_Q3: 2.5, Article_Q4: 2.25, Article_ChuaQ: 2.0,
        Project_NhaNuoc: 3.0, Project_Bo: 2.0, Project_CoSo: 1.0,
        Book_ChuyenKhao: 3.0, Book_GiaoTrinh: 2.0, Book_ThamKhao: 1.5, Book_HuongDan: 1.0,
        Other_Research: 1.0
    });

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const userRoleName = useMemo(() => (currentUser.RoleName || '').toLowerCase(), [currentUser.RoleName]);

    const isManager = useMemo(() =>
        currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
        currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa'),
        [userRoleName, currentUser.RoleId]
    );

    const authHeaders = useMemo(() => ({
        'X-User-Role-Id': currentUser.RoleId ? currentUser.RoleId.toString() : '',
        'X-User-Dept-Id': currentUser.DepartmentId ? currentUser.DepartmentId.toString() : '',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    }), [currentUser]);

    const setupDefaultForm = useCallback((dataList) => {
        if (dataList && dataList.length > 0) {
            const todayStr = new Date().toISOString().split('T')[0];
            const activeConfig = dataList.find(c => {
                const from = c.ApplyFromDate;
                const to = c.ApplyToDate;
                return (from <= todayStr) && (!to || to >= todayStr);
            });

            const configToLoad = activeConfig || dataList[0];
            try {
                const parsedConfig = JSON.parse(configToLoad.ConfigDataJSON);
                setScoringConfig(prev => ({ ...prev, ...parsedConfig }));
            } catch (e) { console.error("Lỗi parse json", e); }
        }
        setNewConfigMeta({ VersionName: '', ApplyFromDate: null, ApplyToDate: null });
        setFormMode('create');
        setActiveConfigId(null);
    }, []);

    const loadConfigVersions = useCallback(() => {
        if (!isManager) return;
        fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoringmanagement?action=getconfig`, {
            headers: authHeaders
        })
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setConfigVersions(data);
                    setupDefaultForm(data);
                }
            })
            .catch(err => console.error("Lỗi tải cấu hình:", err));
    }, [authHeaders, isManager, setupDefaultForm]);

    useEffect(() => {
        loadConfigVersions();
    }, [loadConfigVersions]);

    const loadDataToForm = (ver) => {
        setNewConfigMeta({
            VersionName: ver.VersionName,
            ApplyFromDate: ver.ApplyFromDate ? new Date(ver.ApplyFromDate) : null,
            ApplyToDate: ver.ApplyToDate ? new Date(ver.ApplyToDate) : null
        });
        try {
            setScoringConfig(JSON.parse(ver.ConfigDataJSON));
        } catch (e) { console.error("Lỗi parse json lịch sử", e); }
    };

    const handleViewHistory = (ver) => {
        setFormMode('view');
        setActiveConfigId(ver.Id);
        loadDataToForm(ver);
    };

    const handleEditConfig = (ver) => {
        setFormMode('edit');
        setActiveConfigId(ver.Id);
        loadDataToForm(ver);
    };

    const handleDeleteConfig = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa bản ghi cấu hình này?\nLưu ý: Nếu cấu hình này đã được áp dụng để tính điểm cho giảng viên, hệ thống sẽ chặn thao tác xóa.")) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoringmanagement?action=deleteconfig&id=${id}`, {
                    method: 'DELETE',
                    headers: authHeaders
                });
                const data = await res.json();
                if (data.status === 'success') {
                    toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa cấu hình!' });
                    loadConfigVersions();
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: data.message });
                }
            } catch (error) {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể kết nối máy chủ.' });
            }
        }
    };

    const handleCloneToNew = () => {
        setFormMode('create');
        setActiveConfigId(null);
        setNewConfigMeta(prev => ({
            ...prev,
            VersionName: `Copy của ${prev.VersionName}`,
            ApplyFromDate: null,
            ApplyToDate: null
        }));
        toast.current?.show({ severity: 'info', summary: 'Thông báo', detail: 'Đã sao chép. Vui lòng chọn ngày áp dụng mới để ban hành.' });
    };

    const handleSaveConfig = async () => {
        if (!newConfigMeta.VersionName.trim()) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập tên phiên bản cấu hình!' });
        }
        if (!newConfigMeta.ApplyFromDate) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng chọn ngày bắt đầu áp dụng!' });
        }

        setIsSavingConfig(true);

        const formatDateToLocalString = (date) => {
            if (!date) return null;
            const d = new Date(date);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };

        const payload = {
            VersionName: newConfigMeta.VersionName,
            ApplyFromDate: formatDateToLocalString(newConfigMeta.ApplyFromDate),
            ApplyToDate: formatDateToLocalString(newConfigMeta.ApplyToDate),
            ConfigScores: scoringConfig
        };

        if (isEditMode) {
            payload.Id = activeConfigId;
        }

        const endpoint = isEditMode ? 'updateconfig' : 'saveconfig';

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoringmanagement?action=${endpoint}`, {
                method: 'POST',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            if (data.status === 'success') {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: isEditMode ? 'Đã cập nhật quy chế!' : 'Đã ban hành quy chế mới!' });
                loadConfigVersions();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: data.message });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi mạng', detail: error.message });
        } finally {
            setIsSavingConfig(false);
        }
    };

    return (
        <div className="page-container">
            <style>{`
                .p-datepicker { z-index: 9999 !important; }
                .history-card { transition: all 0.2s ease-in-out; }
                .history-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important; }
                .btn-action-small { background: none; border: none; font-size: 12px; cursor: pointer; padding: 4px 8px; border-radius: 4px; transition: 0.2s; }
                .btn-action-small.edit { color: #f39c12; border: 1px solid #f39c12; }
                .btn-action-small.edit:hover { background: #f39c12; color: #fff; }
                .btn-action-small.delete { color: #e74c3c; border: 1px solid #e74c3c; }
                .btn-action-small.delete:hover { background: #e74c3c; color: #fff; }
            `}</style>
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>CƠ CHẾ TÍNH ĐIỂM KHOA HỌC</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: '25px', alignItems: 'flex-start' }}>

                <div style={{ flex: '1.4', padding: isDesktop ? '25px' : '15px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                    <h4 style={{ margin: '0 0 15px 0', color: isViewMode ? '#8e44ad' : (isEditMode ? '#f39c12' : '#2980b9') }}>
                        <i className={`fa-solid ${isViewMode ? 'fa-eye' : (isEditMode ? 'fa-pen-to-square' : 'fa-plus-circle')}`}></i>
                        {isViewMode ? ' Chi tiết quy chế' : (isEditMode ? ' Chỉnh sửa quy chế' : ' Ban hành quy chế mới')}
                    </h4>

                    <div style={{ marginBottom: '20px', backgroundColor: isViewMode ? '#f9ebff' : (isEditMode ? '#fcf3cf' : '#e8f4f8'), padding: '10px 15px', borderRadius: '5px', borderLeft: `4px solid ${isViewMode ? '#9b59b6' : (isEditMode ? '#f1c40f' : '#3498db')}`, fontSize: '13px', color: isViewMode ? '#6c3483' : (isEditMode ? '#d35400' : '#31708f') }}>
                        {isViewMode ? (
                            <span><strong>Lưu ý:</strong> Bạn đang ở chế độ xem lịch sử. Để sử dụng lại thông số này, hãy nhấn <strong>"Sao chép để tạo mới"</strong>.</span>
                        ) : isEditMode ? (
                            <span><strong>Lưu ý quan trọng:</strong> Bạn đang <strong>SỬA</strong> một quy chế hiện có. Sau khi lưu, vui lòng quay lại trang Quản lý điểm và <strong>Tính lại điểm</strong> cho các giảng viên bị ảnh hưởng để dữ liệu được đồng bộ.</span>
                        ) : (
                            <span><strong>Lưu ý:</strong> Việc ban hành phiên bản mới sẽ không làm thay đổi điểm số cũ. Cấu hình mới sẽ tự động được áp dụng dựa trên khoảng thời gian hiệu lực.</span>
                        )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px', marginBottom: '25px' }}>
                        <div className="form-group">
                            <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>Tên phiên bản quy chế *</label>
                            <input type="text" className="form-input" placeholder="VD: Quy chế tính điểm NCKH Năm học 2025-2026" value={newConfigMeta.VersionName} onChange={(e) => setNewConfigMeta({ ...newConfigMeta, VersionName: e.target.value })} disabled={isViewMode} required />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: '15px' }}>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>Áp dụng từ ngày *</label>
                                <Calendar
                                    value={newConfigMeta.ApplyFromDate}
                                    onChange={(e) => setNewConfigMeta({ ...newConfigMeta, ApplyFromDate: e.value })}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    className="custom-prime-calendar"
                                    style={{ width: '100%', height: '40px' }}
                                    inputStyle={{ padding: '10px 12px', fontSize: '14px', borderRadius: '4px 0 0 4px', border: '1px solid #ccc' }}
                                    disabled={isViewMode}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>Đến ngày (Bỏ trống = Vô thời hạn)</label>
                                <Calendar
                                    value={newConfigMeta.ApplyToDate}
                                    onChange={(e) => setNewConfigMeta({ ...newConfigMeta, ApplyToDate: e.value })}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    className="custom-prime-calendar"
                                    style={{ width: '100%', height: '40px' }}
                                    inputStyle={{ padding: '10px 12px', fontSize: '14px', borderRadius: '4px 0 0 4px', border: '1px solid #ccc' }}
                                    disabled={isViewMode}
                                />
                            </div>
                        </div>
                    </div>

                    <h5 style={{ borderBottom: '2px solid #ddd', paddingBottom: '8px', color: '#2c3e50', marginBottom: '15px', marginTop: '10px' }}>Thông số cấu hình điểm</h5>

                    <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: '15px', marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                        <div className="form-group">
                            <label style={{ fontWeight: 'bold', fontSize: '13px' }}>Quy đổi (1 Điểm = ? Giờ)</label>
                            <input type="number" className="form-input" value={scoringConfig.HoursPerPoint} onChange={(e) => setScoringConfig({ ...scoringConfig, HoursPerPoint: parseFloat(e.target.value) || 0 })} disabled={isViewMode} />
                        </div>
                        <div className="form-group">
                            <label style={{ fontWeight: 'bold', fontSize: '13px' }}>Tỉ lệ chia quyền tác giả chính</label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: '10px' }}>1 /</span>
                                <input type="number" step="0.1" className="form-input" value={scoringConfig.PrimaryAuthorDivisor} onChange={(e) => setScoringConfig({ ...scoringConfig, PrimaryAuthorDivisor: parseFloat(e.target.value) || 3.0 })} disabled={isViewMode} />
                            </div>
                        </div>
                        <div className="form-group">
                            <label style={{ fontWeight: 'bold', fontSize: '13px' }}>Hệ số phạt (Đa cơ quan)</label>
                            <input type="number" step="0.1" className="form-input" value={scoringConfig.MultiOrgPenalty} onChange={(e) => setScoringConfig({ ...scoringConfig, MultiOrgPenalty: parseFloat(e.target.value) || 0.5 })} disabled={isViewMode} />
                        </div>
                        <div className="form-group">
                            <label style={{ fontWeight: 'bold', fontSize: '13px' }}>Điểm NCKH Khác (Base)</label>
                            <input type="number" step="0.1" className="form-input" value={scoringConfig.Other_Research} onChange={(e) => setScoringConfig({ ...scoringConfig, Other_Research: parseFloat(e.target.value) || 1.0 })} disabled={isViewMode} />
                        </div>
                    </div>

                    <h5 style={{ color: '#2c3e50', fontSize: '14px', marginBottom: '10px' }}>Điểm Bài báo Khoa học</h5>

                    <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)', gap: '10px', marginBottom: '15px' }}>
                        <div className="form-group"><label style={{ fontSize: '12px' }}>Hạng Q1</label><input type="number" step="0.25" className="form-input" value={scoringConfig.Article_Q1} onChange={(e) => setScoringConfig({ ...scoringConfig, Article_Q1: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                        <div className="form-group"><label style={{ fontSize: '12px' }}>Hạng Q2</label><input type="number" step="0.25" className="form-input" value={scoringConfig.Article_Q2} onChange={(e) => setScoringConfig({ ...scoringConfig, Article_Q2: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                        <div className="form-group"><label style={{ fontSize: '12px' }}>Hạng Q3</label><input type="number" step="0.25" className="form-input" value={scoringConfig.Article_Q3} onChange={(e) => setScoringConfig({ ...scoringConfig, Article_Q3: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                        <div className="form-group"><label style={{ fontSize: '12px' }}>Hạng Q4</label><input type="number" step="0.25" className="form-input" value={scoringConfig.Article_Q4} onChange={(e) => setScoringConfig({ ...scoringConfig, Article_Q4: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                        <div className="form-group"><label style={{ fontSize: '12px' }}>Chưa phân hạng</label><input type="number" step="0.25" className="form-input" value={scoringConfig.Article_ChuaQ} onChange={(e) => setScoringConfig({ ...scoringConfig, Article_ChuaQ: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: '15px' }}>
                        <div>
                            <h5 style={{ color: '#2c3e50', fontSize: '14px', marginBottom: '10px' }}>Điểm Đề tài</h5>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><label style={{ fontSize: '12px', margin: 0 }}>Cấp Nhà nước</label><input type="number" step="0.5" className="form-input" style={{ width: '80px' }} value={scoringConfig.Project_NhaNuoc} onChange={(e) => setScoringConfig({ ...scoringConfig, Project_NhaNuoc: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><label style={{ fontSize: '12px', margin: 0 }}>Cấp Bộ, Tỉnh</label><input type="number" step="0.5" className="form-input" style={{ width: '80px' }} value={scoringConfig.Project_Bo} onChange={(e) => setScoringConfig({ ...scoringConfig, Project_Bo: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><label style={{ fontSize: '12px', margin: 0 }}>Cấp Cơ sở/Trường</label><input type="number" step="0.5" className="form-input" style={{ width: '80px' }} value={scoringConfig.Project_CoSo} onChange={(e) => setScoringConfig({ ...scoringConfig, Project_CoSo: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                            </div>
                        </div>
                        <div>
                            <h5 style={{ color: '#2c3e50', fontSize: '14px', marginBottom: '10px', marginTop: isDesktop ? '0' : '10px' }}>Điểm Sách</h5>

                            <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><label style={{ fontSize: '12px', margin: 0 }}>Chuyên khảo</label><input type="number" step="0.5" className="form-input" style={{ width: '80px' }} value={scoringConfig.Book_ChuyenKhao} onChange={(e) => setScoringConfig({ ...scoringConfig, Book_ChuyenKhao: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><label style={{ fontSize: '12px', margin: 0 }}>Giáo trình</label><input type="number" step="0.5" className="form-input" style={{ width: '80px' }} value={scoringConfig.Book_GiaoTrinh} onChange={(e) => setScoringConfig({ ...scoringConfig, Book_GiaoTrinh: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><label style={{ fontSize: '12px', margin: 0 }}>Tham khảo</label><input type="number" step="0.5" className="form-input" style={{ width: '80px' }} value={scoringConfig.Book_ThamKhao} onChange={(e) => setScoringConfig({ ...scoringConfig, Book_ThamKhao: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><label style={{ fontSize: '12px', margin: 0 }}>Hướng dẫn</label><input type="number" step="0.5" className="form-input" style={{ width: '80px' }} value={scoringConfig.Book_HuongDan} onChange={(e) => setScoringConfig({ ...scoringConfig, Book_HuongDan: parseFloat(e.target.value) || 0 })} disabled={isViewMode} /></div>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '25px', backgroundColor: '#fdfbf7', border: '1px solid #e8daef', borderRadius: '8px', padding: '15px' }}>
                        <h5 style={{ color: '#8e44ad', margin: '0 0 10px 0', fontSize: '14px', borderBottom: '1px solid #f4ecf7', paddingBottom: '8px' }}>
                            <i className="fa-solid fa-calculator"></i> Diễn giải luồng tính điểm (Cập nhật trực tiếp theo thông số)
                        </h5>
                        <div style={{ fontSize: '12.5px', color: '#555', lineHeight: '1.6', display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: '15px' }}>
                            <div>
                                <p style={{ margin: '0 0 5px 0' }}><strong style={{ color: '#2980b9' }}>1. Bài báo khoa học & Sách:</strong></p>
                                <ul style={{ margin: '0 0 10px 0', paddingLeft: '20px' }}>
                                    <li><strong>Điểm cơ sở:</strong> Lấy theo Hạng Q (Tối đa {scoringConfig.Article_Q1}) × Hệ số thưởng (Hoặc theo Phân loại đối với Sách).</li>
                                    <li><strong>Quyền tác giả:</strong> Nhóm tác giả chính (tác giả đầu & liên hệ) nhận <strong>1/{scoringConfig.PrimaryAuthorDivisor}</strong> tổng điểm. Phần còn lại chia đều cho <strong>Tất cả</strong> tác giả.</li>
                                    <li><strong>Sinh viên tham gia:</strong> Điểm của Sinh viên sẽ được chuyển và <strong>chia đều</strong> cho các GV Hướng dẫn.</li>
                                    <li><strong>Đa cơ quan:</strong> Nhân thêm hệ số phạt <strong>{scoringConfig.MultiOrgPenalty}</strong> nếu ghi nhận từ 2 cơ quan trở lên.</li>
                                </ul>
                            </div>
                            <div>
                                <p style={{ margin: '0 0 5px 0' }}><strong style={{ color: '#2980b9' }}>2. Đề tài NCKH:</strong></p>
                                <ul style={{ margin: '0 0 10px 0', paddingLeft: '20px' }}>
                                    <li><strong>Điểm cơ sở:</strong> Lấy theo Cấp quản lý. Áp dụng 100% cho <strong>Chủ nhiệm</strong> đề tài (Các thành viên khác tạm tính 0 điểm).</li>
                                </ul>

                                <p style={{ margin: '0 0 5px 0' }}><strong style={{ color: '#2980b9' }}>3. Sở hữu trí tuệ & NCKH Khác:</strong></p>
                                <ul style={{ margin: '0 0 10px 0', paddingLeft: '20px' }}>
                                    <li><strong>Điểm cơ sở:</strong> Lấy theo Điểm quy định riêng (VD: Sáng chế 3đ, Giải pháp 2đ...) × Số lượng / Số thành viên tham gia.</li>
                                </ul>

                                <div style={{ marginTop: '10px', backgroundColor: '#fcf3cf', padding: '8px', borderRadius: '4px', borderLeft: '3px solid #f1c40f' }}>
                                    <p style={{ margin: 0, color: '#c0392b' }}>
                                        Tổng giờ = Tổng Điểm NCKH đạt được × <strong>{scoringConfig.HoursPerPoint}</strong> giờ/điểm
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '25px', display: 'flex', justifyContent: isDesktop ? 'flex-end' : 'center', flexWrap: 'wrap', gap: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                        {isViewMode ? (
                            <>
                                <button type="button" className="btn-cancel" style={{ margin: 0 }} onClick={() => setupDefaultForm(configVersions)}>
                                    <i className="fa-solid fa-arrow-left" style={{ marginRight: '5px' }}></i> Trở về
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#9b59b6', margin: 0, minWidth: '150px' }} onClick={handleCloneToNew}>
                                    <i className="fa-solid fa-copy" style={{ marginRight: '5px' }}></i> Sao chép để tạo mới
                                </button>
                            </>
                        ) : (
                            <>
                                {isEditMode && (
                                    <button type="button" className="btn-cancel" style={{ margin: 0 }} onClick={() => setupDefaultForm(configVersions)}>
                                        Hủy chỉnh sửa
                                    </button>
                                )}
                                <button type="button" className="btn-submit" style={{ backgroundColor: isEditMode ? '#f39c12' : '#27ae60', margin: 0, minWidth: '150px' }} onClick={handleSaveConfig} disabled={isSavingConfig}>
                                    {isSavingConfig ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-file-signature"></i>}
                                    {isSavingConfig ? ' Đang lưu...' : (isEditMode ? ' Cập nhật quy chế' : ' Ban hành quy chế mới')}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div style={{ flex: '1', width: '100%', padding: isDesktop ? '25px' : '15px', backgroundColor: '#fafafa', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #eee' }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#555' }}><i className="fa-solid fa-clock-rotate-left"></i> Lịch sử ban hành</h4>
                    {configVersions.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px', color: '#999' }}>Chưa có dữ liệu lịch sử</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {configVersions.map((ver, idx) => {
                                const isActive = (ver.ApplyFromDate && ver.ApplyFromDate <= today) && (!ver.ApplyToDate || ver.ApplyToDate >= today);
                                const isLatest = idx === 0;
                                const isSelected = activeConfigId === ver.Id;

                                return (
                                    <div key={ver.Id}
                                        className="history-card"
                                        onClick={() => handleViewHistory(ver)}
                                        style={{
                                            backgroundColor: isSelected ? (isEditMode ? '#fef9e7' : '#f5ebfa') : '#fff',
                                            border: isSelected ? `1px solid ${isEditMode ? '#f1c40f' : '#9b59b6'}` : '1px solid #e0e0e0',
                                            borderRadius: '6px',
                                            padding: '15px',
                                            position: 'relative',
                                            cursor: 'pointer',
                                            boxShadow: isActive ? '0 2px 8px rgba(46, 204, 113, 0.2)' : (isSelected ? '0 2px 8px rgba(155, 89, 182, 0.2)' : 'none'),
                                            borderLeft: isSelected ? `4px solid ${isEditMode ? '#f39c12' : '#9b59b6'}` : (isActive ? '4px solid #2ecc71' : (isLatest ? '4px solid #3498db' : '1px solid #e0e0e0'))
                                        }}
                                    >
                                        <div style={{ position: 'absolute', top: '-10px', right: '15px', display: 'flex', gap: '8px' }}>
                                            {isActive && (
                                                <span style={{ backgroundColor: '#2ecc71', color: '#fff', fontSize: '10px', padding: '3px 8px', borderRadius: '10px', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(46,204,113,0.3)' }}>
                                                    <i className="fa-solid fa-check-circle" style={{ marginRight: '4px' }}></i> ĐANG ÁP DỤNG
                                                </span>
                                            )}
                                            {isLatest && (
                                                <span style={{ backgroundColor: '#3498db', color: '#fff', fontSize: '10px', padding: '3px 8px', borderRadius: '10px', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(52,152,219,0.3)' }}>
                                                    MỚI NHẤT
                                                </span>
                                            )}
                                        </div>

                                        <h5 style={{ margin: '0 0 8px 0', color: isSelected ? (isEditMode ? '#d35400' : '#8e44ad') : (isActive ? '#27ae60' : '#2c3e50'), fontSize: '14px' }}>
                                            {ver.VersionName}
                                        </h5>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                                            <i className="fa-regular fa-calendar-check" style={{ width: '15px', color: '#3498db' }}></i> Áp dụng từ: <strong>{ver.ApplyFromDate ? new Date(ver.ApplyFromDate).toLocaleDateString('vi-VN') : '---'}</strong>
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                                            <i className="fa-regular fa-calendar-xmark" style={{ width: '15px', color: '#e74c3c' }}></i> Đến ngày: {ver.ApplyToDate ? new Date(ver.ApplyToDate).toLocaleDateString('vi-VN') : <em>Vô thời hạn</em>}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#999', borderTop: '1px dashed #eee', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>Tạo lúc: {ver.CreatedAt}</span>
                                            {ver.IsEditable ? (
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <button className="btn-action-small edit" onClick={(e) => { e.stopPropagation(); handleEditConfig(ver); }}>
                                                        <i className="fa-solid fa-pen"></i> Sửa
                                                    </button>
                                                    <button className="btn-action-small delete" onClick={(e) => { e.stopPropagation(); handleDeleteConfig(ver.Id); }}>
                                                        <i className="fa-solid fa-trash"></i> Xóa
                                                    </button>
                                                </div>
                                            ) : (
                                                <span style={{ fontStyle: 'italic', color: '#e74c3c' }} title="Đã qua 7 ngày kể từ khi tạo">
                                                    <i className="fa-solid fa-lock"></i> Đã khóa
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScoringConfiguration;